import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";

const baseUrl = process.env.REACT_APP_API_URL;

export function webAuthnSupported(): boolean {
  return browserSupportsWebAuthn();
}

function readCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Cookie-authenticated mutations are double-submit protected, so the CSRF
// cookie has to be echoed back in a header the browser cannot forge.
function headers(): Record<string, string> {
  const result: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const csrf = readCsrfToken();
  if (csrf) result["X-CSRF-Token"] = csrf;
  return result;
}

async function call(path: string, method: string, body?: unknown) {
  const response = await fetch(`${baseUrl}${path}`, {
    method,
    headers: headers(),
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const data = response.status === 204 ? null : await response.json().catch(() => null);
  return { data, status: response.status };
}

export class WebAuthnCancelled extends Error {}

// The browser throws NotAllowedError for a user dismissing the prompt AND for
// environment failures (a key that cannot satisfy required user verification,
// no reachable authenticator). A genuine dismissal takes human time; a
// near-instant failure is an environment problem and deserves a message.
const INSTANT_FAILURE_MS = 1500;

function asCancellation(error: unknown, startedAt: number): never {
  const name = (error as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "AbortError") {
    if (Date.now() - startedAt > INSTANT_FAILURE_MS) {
      throw new WebAuthnCancelled("Ceremony cancelled");
    }
    throw new Error(
      "the browser ended the ceremony immediately — usually the security key " +
        "has no PIN set (verification is required) or no authenticator is reachable",
    );
  }
  throw error;
}

export async function loginWithSecurityKey() {
  const options = await call("/platform/webauthn/login/options", "POST");
  if (options.status !== 200) {
    throw new Error("Could not start the security key sign-in");
  }

  const startedAt = Date.now();
  let credential;
  try {
    credential = await startAuthentication({ optionsJSON: options.data });
  } catch (error) {
    asCancellation(error, startedAt);
  }

  return call("/platform/webauthn/login/verify", "POST", { credential });
}

export async function enrolSecurityKey(nickname: string) {
  const options = await call("/platform/self/webauthn/register/options", "POST");
  if (options.status !== 200) {
    throw new Error("Could not start security key enrolment");
  }

  const startedAt = Date.now();
  let credential;
  try {
    credential = await startRegistration({ optionsJSON: options.data });
  } catch (error) {
    asCancellation(error, startedAt);
  }

  return call("/platform/self/webauthn/register/verify", "POST", {
    credential,
    nickname,
  });
}

export async function listSecurityKeys() {
  return call("/platform/self/webauthn/credentials", "GET");
}

export async function renameSecurityKey(id: number, nickname: string) {
  return call(`/platform/self/webauthn/credentials/${id}`, "PATCH", { nickname });
}

export async function removeSecurityKey(id: number) {
  return call(`/platform/self/webauthn/credentials/${id}`, "DELETE");
}
